import { Link } from 'react-router-dom'
import { CaretRight } from '@phosphor-icons/react'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { Badge, Banner, Card, CardBody, CardHeader, EmptyState } from '@/ui'
import type { ProcessState } from '@/features/process/projection'
import { isRunLive } from '@/features/process/projection'
import { ProcessScreen } from '@/features/process/components/ProcessScreen'
import { SyncBadges } from '@/features/process/components/bits'
import { LotStateBadge } from '../components/LotStateBadge'

/** Pack line: the pack runs planned for this hall, the ones running, and pouches to empty. */
export function PackHomePage() {
  return <ProcessScreen title="pack.home.title">{(state) => <PackHome state={state} />}</ProcessScreen>
}

function PackHome({ state }: { state: ProcessState }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const runs = [...state.packRuns.values()].filter((r) => r.status !== 'CANCELLED')
  const live = runs.filter((r) => isRunLive(r.status))
  const done = runs.filter((r) => !isRunLive(r.status))
  const delabel = [...state.runs.values()].filter((r) => r.step_code === 'DELABEL' && isRunLive(r.status))

  const runStage = (r: (typeof runs)[number]): MessageKey =>
    !r.started_at ? 'pack.stage.planned' : r.close ? 'pack.stage.closed' : isRunLive(r.status) ? 'pack.stage.filling' : 'pack.stage.closed'

  return (
    <div className="flex flex-col gap-4">
      {delabel.length > 0 && (
        <Card>
          <CardHeader eyebrow={t('pack.delabel.list')} />
          <CardBody className="py-0">
            <ul>
              {delabel.map((r) => (
                <li key={r.id}>
                  <Link to={`/m/pack/delabel/${r.id}`} className="flex items-center gap-3 border-b border-rule/70 py-3 last:border-b-0">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[15px] font-bold">{String(r.params.pack_lot_code ?? r.code)}</div>
                      <div className="text-[13px] text-muted">{t('pack.delabel.pouches', { n: Number(r.params.pouches ?? 0) })}</div>
                    </div>
                    <Badge tone="danger">{t('pack.delabel.badge')}</Badge>
                    <CaretRight aria-hidden="true" className="text-muted" />
                  </Link>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
      <Card>
        <CardHeader eyebrow={t('pack.home.runs', { n: live.length })} />
        <CardBody className="py-0">
          {live.length === 0 ? (
            <EmptyState compact title={t('pack.home.none')} description={t('pack.home.noneBody')} />
          ) : (
            <ul>
              {live.map((r) => (
                <li key={r.id}>
                  <Link to={`/m/pack/run/${r.id}`} className="flex items-center gap-3 border-b border-rule/70 py-3 last:border-b-0">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-[15px] font-bold">{r.sku.name}</span>
                        <span className="font-mono text-[12px] text-muted">{r.code}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px] text-muted">
                        <Badge tone={r.started_at ? 'info' : 'neutral'}>{t(runStage(r))}</Badge>
                        <span className="font-mono">{r.batch_code}</span>
                        <span>{t('pack.pouchesPlanned', { n: r.planned_pouches })}</span>
                        <span>{fmt.weight(r.planned_g)}</span>
                        {r.batch_hold !== 'NONE' && <Badge tone="warn">{t('pack.badge.batchHeld')}</Badge>}
                        <SyncBadges sync={r.sync} />
                      </div>
                    </div>
                    <CaretRight aria-hidden="true" className="text-muted" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
      {done.length > 0 && (
        <Card>
          <CardHeader eyebrow={t('pack.home.closed')} />
          <CardBody className="py-0">
            <ul>
              {done.slice(0, 10).map((r) => (
                <li key={r.id}>
                  <Link to={`/m/pack/run/${r.id}`} className="flex items-center gap-3 border-b border-rule/70 py-3 last:border-b-0">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[14px] font-bold">{r.pack_lot?.code ?? r.code}</div>
                      <div className="text-[12px] text-muted">{r.sku.name}</div>
                    </div>
                    {r.pack_lot && <LotStateBadge lot={state.packLots.get(r.pack_lot.id) ?? r.pack_lot} />}
                    <CaretRight aria-hidden="true" className="text-muted" />
                  </Link>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
      <Banner tone="info" action={<Link to="/m/pack/lots" className="font-mono text-[12px] font-bold uppercase underline">{t('pack.lots.title')}</Link>}>
        {t('pack.home.lotsHint')}
      </Banner>
    </div>
  )
}
